'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Copy, RefreshCw, Check, Plus, Minus, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomXmlGeneratorContent } from '../../data/random-xml-generator-content';
import Link from 'next/link';

type XmlNode = {
    id: string;
    name: string;
    attributes: { [key: string]: string };
    children: XmlNode[];
    value?: string;
    isExpanded?: boolean;
};

const DEFAULT_XML_STRUCTURE: XmlNode = {
    id: '1',
    name: 'root',
    attributes: {},
    children: [
        {
            id: '2',
            name: 'item',
            attributes: { id: '1' },
            children: [
                { id: '3', name: 'name', attributes: {}, value: 'Item 1', children: [] },
                { id: '4', name: 'price', attributes: { currency: 'USD' }, value: '9.99', children: [] },
                { id: '5', name: 'inStock', attributes: {}, value: 'true', children: [] },
            ],
        },
    ],
};

const generateRandomString = (length: number = 8): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

const generateRandomValue = (type: 'string' | 'number' | 'boolean' = 'string'): string => {
    switch (type) {
        case 'number':
            return (Math.random() * 100).toFixed(2);
        case 'boolean':
            return Math.random() > 0.5 ? 'true' : 'false';
        default:
            return generateRandomString(6);
    }
};

const generateRandomAttributes = (count: number = 2): { [key: string]: string } => {
    const attributes: { [key: string]: string } = {};
    const attrNames = ['id', 'type', 'category', 'status', 'version'];

    for (let i = 0; i < Math.min(count, attrNames.length); i++) {
        attributes[attrNames[i]] = generateRandomValue();
    }

    return attributes;
};

const generateRandomXmlNode = (depth: number = 0, maxDepth: number = 3): XmlNode => {
    const nodeNames = ['item', 'product', 'entry', 'record', 'data', 'element'];
    const hasChildren = depth < maxDepth && Math.random() > 0.5;

    const node: XmlNode = {
        id: Math.random().toString(36).substring(2, 9),
        name: nodeNames[Math.floor(Math.random() * nodeNames.length)],
        attributes: generateRandomAttributes(1 + Math.floor(Math.random() * 2)),
        children: [],
        isExpanded: true,
    };

    if (!hasChildren) {
        const valueTypes: ('string' | 'number' | 'boolean')[] = ['string', 'number', 'boolean'];
        const valueType = valueTypes[Math.floor(Math.random() * valueTypes.length)];
        node.value = generateRandomValue(valueType);
    } else {
        const childCount = 1 + Math.floor(Math.random() * 3);
        for (let i = 0; i < childCount; i++) {
            node.children.push(generateRandomXmlNode(depth + 1, maxDepth));
        }
    }

    return node;
};

const formatXml = (xml: string): string => {
    let formatted = '';
    let indent = '';
    const tab = '  ';
    let i = 0;

    xml = xml.replace(/\s*<\/?\w+[^>]*>\s*/g, (m) => m.trim());

    while (i < xml.length) {
        const char = xml[i];

        if (char === '<') {
            if (xml[i + 1] === '/') {
                // Closing tag
                indent = indent.slice(0, -tab.length);
                formatted += '\n' + indent;
            } else {
                // Opening tag
                formatted += '\n' + indent;
                indent += tab;
            }

            // Find the end of the tag
            const end = xml.indexOf('>', i) + 1;
            formatted += xml.substring(i, end);
            i = end;
        } else {
            // Text content
            const nextTag = xml.indexOf('<', i);
            if (nextTag === -1) break;

            const text = xml.substring(i, nextTag).trim();
            if (text) {
                formatted += '\n' + indent + text;
            }
            i = nextTag;
        }
    }

    return formatted.trim();
};

const xmlToString = (node: XmlNode, indent: string = ''): string => {
    let xml = `${indent}<${node.name}`;

    // Add attributes
    for (const [key, value] of Object.entries(node.attributes)) {
        xml += ` ${key}="${value}"`;
    }

    if (node.children.length === 0 && !node.value) {
        // Self-closing tag
        xml += ' />';
    } else {
        // Opening tag
        xml += '>';

        // Add value if exists
        if (node.value) {
            xml += node.value;
        }

        // Add children
        if (node.children.length > 0) {
            for (const child of node.children) {
                xml += '\n' + xmlToString(child, indent + '  ');
            }
            xml += '\n' + indent;
        }

        // Closing tag
        xml += `</${node.name}>`;
    }

    return xml;
};

const XmlNodeEditor = ({
    node,
    onChange,
    onAddChild,
    onRemove,
    onToggleExpand,
    depth = 0,
}: {
    node: XmlNode;
    onChange: (node: XmlNode) => void;
    onAddChild: () => void;
    onRemove: () => void;
    onToggleExpand: () => void;
    depth?: number;
}) => {
    const hasChildren = node.children.length > 0;
    const isSelfClosing = !hasChildren && !node.value;

    return (
        <div className="space-y-2">
            <div className="flex items-center space-x-2">
                {hasChildren && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={onToggleExpand}
                    >
                        {node.isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </Button>
                )}
                {!hasChildren && <div className="w-6" />}

                <div className="flex-1 flex items-center space-x-2 bg-muted/50 p-2 rounded">
                    <span className="text-blue-500">&lt;{node.name}</span>

                    {Object.entries(node.attributes).map(([key, value]) => (
                        <div key={key} className="flex items-center">
                            <span className="text-purple-500"> {key}=</span>
                            <span className="text-green-600">"{value}"</span>
                        </div>
                    ))}

                    {isSelfClosing && <span className="text-blue-500"> /&gt;</span>}
                    {!isSelfClosing && (
                        <>
                            <span className="text-blue-500">&gt;</span>
                            {node.value && <span className="text-foreground">{node.value}</span>}
                            {!node.value && hasChildren && !node.isExpanded && <span className="text-muted-foreground">...</span>}
                            <span className="text-blue-500">&lt;/{node.name}&gt;</span>
                        </>
                    )}
                </div>

                <Button variant="ghost" size="sm" onClick={onAddChild} className="h-8 w-8 p-0">
                    <Plus className="h-4 w-4" />
                </Button>

                {depth > 0 && (
                    <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0 text-destructive">
                        <Minus className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {hasChildren && node.isExpanded && (
                <div className="pl-6 space-y-2 border-l-2 border-muted">
                    {node.children.map((child) => (
                        <XmlNodeEditor
                            key={child.id}
                            node={child}
                            onChange={(updated) => {
                                const newChildren = [...node.children];
                                const index = newChildren.findIndex((c) => c.id === updated.id);
                                if (index >= 0) {
                                    newChildren[index] = updated;
                                    onChange({ ...node, children: newChildren });
                                }
                            }}
                            onAddChild={() => {
                                const newChild = generateRandomXmlNode();
                                onChange({
                                    ...node,
                                    children: [...node.children, newChild],
                                });
                            }}
                            onRemove={() => {
                                onChange({
                                    ...node,
                                    children: node.children.filter((c) => c.id !== child.id),
                                });
                            }}
                            onToggleExpand={() => {
                                const newChildren = [...node.children];
                                const index = newChildren.findIndex((c) => c.id === child.id);
                                if (index >= 0) {
                                    newChildren[index] = {
                                        ...child,
                                        isExpanded: !child.isExpanded,
                                    };
                                    onChange({ ...node, children: newChildren });
                                }
                            }}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function RandomXmlGenerator() {
    const [xmlStructure, setXmlStructure] = useState<XmlNode>(() => ({
        ...DEFAULT_XML_STRUCTURE,
        isExpanded: true,
    }));

    const [output, setOutput] = useState<string>('');
    const [isFormatted, setIsFormatted] = useState(true);
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const generateXml = useCallback(() => {
        const xml = `<?xml version="1.0" encoding="UTF-8"?>\n${xmlToString(xmlStructure)}`;
        return isFormatted ? formatXml(xml) : xml;
    }, [xmlStructure, isFormatted]);

    const copyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'XML has been copied to clipboard.',
            });
            setTimeout(() => setCopied(false), 2000);
        });
    }, [output, toast]);

    const addRootNode = useCallback(() => {
        const newNode = generateRandomXmlNode(0, 2);
        setXmlStructure({
            ...xmlStructure,
            children: [...xmlStructure.children, newNode],
        });
    }, [xmlStructure]);

    const generateRandomXml = useCallback(() => {
        const newStructure = generateRandomXmlNode(0, 3);
        newStructure.name = 'root';
        newStructure.isExpanded = true;
        setXmlStructure(newStructure);
    }, []);

    // Update output when structure changes
    useEffect(() => {
        try {
            setOutput(generateXml());
        } catch (error) {
            console.error('Error generating XML:', error);
            setOutput('Error generating XML. Please check the structure.');
        }
    }, [generateXml]);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
                <ToolPageIntro title="Random XML Generator">
                Generate random XML data with our free online tool. Create test XML files, mock configurations, and sample XML structures for development and testing.
            </ToolPageIntro>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <CardTitle>XML Structure</CardTitle>
                                    <div className="space-x-2">
                                        <Button variant="outline" size="sm" onClick={addRootNode}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Node
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={generateRandomXml}>
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                            Randomize
                                        </Button>
                                    </div>
                                </div>
                                <CardDescription>
                                    Build your XML structure by adding nodes and attributes.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2 text-blue-500">
                                        <span>&lt;?xml version="1.0" encoding="UTF-8"?&gt;</span>
                                    </div>

                                    <div className="pl-4">
                                        <XmlNodeEditor
                                            node={xmlStructure}
                                            onChange={setXmlStructure}
                                            onAddChild={() => {
                                                const newChild = generateRandomXmlNode(1, 3);
                                                setXmlStructure({
                                                    ...xmlStructure,
                                                    children: [...xmlStructure.children, newChild],
                                                });
                                            }}
                                            onRemove={() => { }}
                                            onToggleExpand={() => {
                                                setXmlStructure({
                                                    ...xmlStructure,
                                                    isExpanded: !xmlStructure.isExpanded,
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>How to Use</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <h3 className="font-medium">1. Build Your Structure</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Add nodes and attributes to build your XML structure. Use the + button to add child nodes.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="font-medium">2. Customize</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Click on nodes to edit their names and values. Use the randomize button to generate a new structure.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="font-medium">3. Copy & Use</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Copy the generated XML to your clipboard and use it in your applications for testing and development.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-6">
                        <Card className="h-full">
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <CardTitle>Generated XML</CardTitle>
                                    <div className="flex items-center space-x-2">
                                        <div className="flex items-center space-x-2">
                                            <Label htmlFor="format-toggle" className="text-sm font-medium">Format</Label>
                                            <div className="flex items-center h-5">
                                                <input
                                                    id="format-toggle"
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    checked={isFormatted}
                                                    onChange={(e) => setIsFormatted(e.target.checked)}
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={copyToClipboard}
                                            disabled={!output}
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4 mr-2 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4 mr-2" />
                                            )}
                                            Copy
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="h-[calc(100%-120px)]">
                                <div className="h-full">
                                    <Textarea
                                        value={output}
                                        onChange={(e) => setOutput(e.target.value)}
                                        className="h-full min-h-[400px] font-mono text-sm"
                                        placeholder="Your XML will appear here..."
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomXmlGeneratorContent.title}
                description={randomXmlGeneratorContent.description}
                content={randomXmlGeneratorContent.content}
                faqs={randomXmlGeneratorContent.faqs}
                relatedTools={randomXmlGeneratorContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
